import React from 'react';
import {
  MasonryEngineNode,
  MasonryEngineNodeRendererProps,
} from '@leagueplatform/masonry-engine';
import {
  MasonryDriverAction,
  useMasonryHeroBannerHeight,
  useInvokeAction,
  MasonryButton,
} from '@leagueplatform/masonry-renderers';
import {
  StackLayout,
  GDSIconSource,
  HeadingText,
  ParagraphText,
  Box,
} from '@leagueplatform/genesis-core';
import {
  getModulePath,
  LEAGUE_MODULE_NAMES,
} from '@leagueplatform/league-routes';
import { BackButtonArrow } from '@leagueplatform/web-common-components';
import { useGoBack } from '@leagueplatform/web-common';
import { PageHeaderBackground } from './components/hero-banner-background.component';

type MasonryHeroBannerNodeProperties = {
  heading?: string;
  body?: string;
  buttonLabel?: string;
  buttonIcon?: GDSIconSource;
  curved?: boolean;
  includeSafeArea?: boolean;
  hideBackButton?: boolean;
};

export type MasonryHeroBannerNodeActions = {
  onButtonClick?: MasonryDriverAction;
  onBackButtonClick?: MasonryDriverAction;
};

export const NODE_TYPE_BENEFITS_HERO_BANNER = 'benefitsHeroBanner' as const;

export type MasonryHeroBannerNode = MasonryEngineNode<
  typeof NODE_TYPE_BENEFITS_HERO_BANNER,
  MasonryHeroBannerNodeProperties,
  MasonryHeroBannerNodeActions
>;

type MasonryHeroBannerRendererProps =
  MasonryEngineNodeRendererProps<MasonryHeroBannerNode>;

const formatClassNames = (...classNames: (string | undefined)[]) =>
  classNames?.filter(Boolean).join(' ') || '';

export const BenefitsHeroBannerRenderer = ({
  heading,
  body,
  buttonLabel,
  buttonIcon,
  curved,
  engineClassName,
  className,
  onButtonClick,
  locatorId,
  hideBackButton,
  onBackButtonClick,
}: MasonryHeroBannerRendererProps) => {
  const fallbackLocation = React.useMemo(
    () => getModulePath(LEAGUE_MODULE_NAMES.benefits),
    [],
  );
  const goBack = useGoBack(fallbackLocation);
  const invokeAction = useInvokeAction();
  const { height, contentRef } = useMasonryHeroBannerHeight();

  const handleBackButtonClick = React.useCallback(
    (event: React.MouseEvent) => {
      event.preventDefault();
      if (onBackButtonClick) {
        invokeAction({
          event,
          ...onBackButtonClick,
        });
      }

      goBack();
    },
    [goBack, invokeAction, onBackButtonClick],
  );

  const handleButtonClick = React.useCallback(
    (event: React.MouseEvent) => {
      if (onButtonClick) {
        invokeAction({
          event,
          ...onButtonClick,
        });
      }
    },
    [invokeAction, onButtonClick],
  );

  return (
    <Box css={{ position: 'relative' }}>
      <PageHeaderBackground
        height={height}
        className={formatClassNames(engineClassName, className)}
        curved={curved}
        data-testid={locatorId}
      >
        <StackLayout ref={contentRef}>
          <Box
            css={{
              display: 'flex',
              gap: '$one',
              '@mobile': { flexDirection: 'column', gap: '$oneAndHalf' },
            }}
          >
            {!hideBackButton && (
              <Box
                css={{
                  '@mobile': { height: '$oneAndHalf' },
                }}
              >
                <BackButtonArrow onClick={handleBackButtonClick} id="go-back" />
              </Box>
            )}
            <StackLayout
              spacing={{
                '@initial': '$oneAndHalf',
                '@laptop': '$one',
                '@desktop': '$one',
              }}
            >
              <StackLayout spacing="$half">
                <HeadingText level="1" size="xl">
                  {heading}
                </HeadingText>
                <ParagraphText>{body}</ParagraphText>
              </StackLayout>
              {buttonLabel && (
                <MasonryButton
                  size="medium"
                  priority="primary"
                  icon={buttonIcon}
                  action={onButtonClick}
                  onClick={handleButtonClick}
                >
                  {buttonLabel}
                </MasonryButton>
              )}
            </StackLayout>
          </Box>
        </StackLayout>
      </PageHeaderBackground>
    </Box>
  );
};
